import type { ResidencyDay } from '@salonomy/types';

/**
 * WS11 — pure display helpers for a residency's weekly working blocks (ResidencyDay[]). These format
 * the SERVER's schedule rows for the salon roster + "My residencies" surfaces; they never compute
 * availability or invent blocks (the schedule is read straight from the membership's working_hours —
 * brief 11 §2; ADR-0012). Kept side-effect-free so a vitest unit can cover them like time.ts / goLive.ts.
 */

/** Mon-first weekday labels; the API uses 0 = Sunday … 6 = Saturday (ResidencyDay.weekday). */
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Mon-first ordering so a working week reads naturally (Mon → Sun), without re-deriving any data. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** A single block's label, e.g. "Mon 09:00–17:00". Times are the server's wall-clock "HH:MM" strings. */
export function residencyDayLabel(day: ResidencyDay): string {
  const name = WEEKDAY_SHORT[day.weekday] ?? `Day ${day.weekday}`;
  return `${name} ${day.startTime}–${day.endTime}`;
}

/**
 * The distinct weekdays this residency covers, Mon-first, e.g. "Mon, Tue, Thu". A residency may carry
 * several blocks on one day (a split shift); we collapse to one chip per day for the at-a-glance summary
 * while the full per-block list stays available via `residencyDayLabel`.
 */
export function scheduleWeekdaySummary(schedule: ResidencyDay[]): string {
  const present = new Set(schedule.map((d) => d.weekday));
  const days = WEEK_ORDER.filter((w) => present.has(w)).map((w) => WEEKDAY_SHORT[w]);
  return days.join(', ');
}

/** The per-block labels in Mon-first order, one entry per working block (split shifts kept distinct). */
export function scheduleBlockLabels(schedule: ResidencyDay[]): string[] {
  return [...schedule]
    .sort(
      (a, b) =>
        orderIndex(a.weekday) - orderIndex(b.weekday) || a.startTime.localeCompare(b.startTime),
    )
    .map(residencyDayLabel);
}

function orderIndex(weekday: number): number {
  const i = WEEK_ORDER.indexOf(weekday as (typeof WEEK_ORDER)[number]);
  return i === -1 ? WEEK_ORDER.length : i;
}
